import {useEffect, useRef, useState} from 'react';
import paper from 'paper';
import {clearSelection} from './paper-system-utils';
import {createShapeFromDrag} from './shape-geometry';
import {
  MIN_DRAG_DISTANCE,
  VECTOR_STROKE_COLOR,
  ANGLE_COEFFICIENT,
  RULER_SIZE,
  SHAPE_TOOLS,
  FILL_COLOR
} from '@/constants/editor';


export function useCanvasInteractions({
                                        activeTool,
                                        setActiveTool,
                                        onProjectChange,
                                        drawBackgroundCanvas,
                                        drawRulers,
                                        updateTransformUI,
                                        calculateSnapDelta,
                                        resetGuidesHighlight,
                                        addTextAsPath,
                                        customGuides,
                                        setCustomGuides,
                                        viewMode,
                                        t,
                                        theme,
}) {
  const activeToolRef = useRef(activeTool);
  const setActiveToolRef = useRef(setActiveTool);
  const customGuidesRef = useRef(customGuides);
  const setCustomGuidesRef = useRef(setCustomGuides);
  const viewModeRef = useRef(viewMode);
  const tRef = useRef(t);
  const themeRef = useRef(theme);

  useEffect(() => {
    activeToolRef.current = activeTool;
    setActiveToolRef.current = setActiveTool;
    viewModeRef.current = viewMode;
    tRef.current = t;
    themeRef.current = theme;
  }, [activeTool, setActiveTool, viewMode, t, theme]);

  useEffect(() => {
    customGuidesRef.current = customGuides;
    setCustomGuidesRef.current = setCustomGuides;
  }, [customGuides, setCustomGuides]);

  const isAltPressedRef = useRef(false);
  const isSpacePressedRef = useRef(false);
  const isPanningRef = useRef(false);
  const [isPanning, setIsPanning] = useState(false);

  const drawingRef = useRef(null);
  const currentPathRef = useRef(null);

  const duplicateDragRef = useRef({armed: false, triggered: false});
  const transformStateRef = useRef({
    action: null,
    initialBounds: null,
    initialCenter: null,
    initialPoint: null,
    initialAngle: null,
  });

  const rafPendingRef = useRef(false);
  const onProjectChangeRef = useRef(onProjectChange);

  useEffect(() => {
    onProjectChangeRef.current = onProjectChange;
  }, [onProjectChange]);

  useEffect(() => {
    return () => {
      rafPendingRef.current = false;
    };
  }, []);

  const selectionStateRef = useRef({
    draggedGuide: null,
  });

  const scheduleProjectChange = () => {
    if (rafPendingRef.current) return;
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      onProjectChangeRef.current?.();
    });
  };

  useEffect(() => {
    if (!paper.project) return;
    const canvas = paper.view.element;

    const handleContextMenu = (e) => e.preventDefault();
    canvas.addEventListener('contextmenu', handleContextMenu);

    const handleDblClick = (e) => {
      if (activeToolRef.current !== 'select') return;

      const point = paper.view.viewToProject(new paper.Point(e.offsetX, e.offsetY));
      const hitResult = paper.project.hitTest(point, {
        fill: true,
        stroke: true,
        tolerance: 10 / paper.view.zoom,
        match: (hit) => (hit.item instanceof paper.PointText) && hit.item.visible && !hit.item.locked
      });

      if (hitResult && hitResult.item) {
        const event = new CustomEvent('edit-text-item', {detail: {item: hitResult.item}});
        window.dispatchEvent(event);
      }
    };
    canvas.addEventListener('dblclick', handleDblClick);

    const tool = new paper.Tool();

    let selectionRect = null;
    let selectionStartPoint = null;
    let isBoxSelecting = false;

    tool.onMouseMove = (event) => {
      const handleHit = paper.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        guides: true,
        tolerance: 10 / paper.view.zoom,
        match: (hit) => hit.item.visible && hit.item.data?.isTransformHandle,
      });

      if (handleHit && handleHit.item.data.cursor) {
        paper.view.element.style.cursor = handleHit.item.data.cursor;
        return;
      } else {
        const cursors = ['nwse-resize', 'ns-resize', 'ew-resize', 'nesw-resize', 'grab'];
        if (cursors.includes(paper.view.element.style.cursor)) {
          paper.view.element.style.cursor = '';
        }
      }

      if (isAltPressedRef.current && activeToolRef.current === 'select') {
        const itemHit = paper.project.hitTest(event.point, {
          fill: true,
          stroke: true,
          tolerance: 10 / paper.view.zoom,
          match: (hit) => {
            return hit.item.selected && !hit.item.guide && !hit.item.data?.isTransformHandle;
          }
        });

        if (itemHit) {
          paper.view.element.style.cursor = 'copy';
          return;
        } else {
          if (paper.view.element.style.cursor === 'copy') {
            paper.view.element.style.cursor = '';
          }
        }
      }

      const guides = customGuidesRef.current;
      const mouseViewPos = new paper.Point(event.event.offsetX, event.event.offsetY);
      let isOverHandle = false;

      for (const guide of guides) {
        const {x, y, angle = 0} = guide;
        const rad = (angle * Math.PI) / ANGLE_COEFFICIENT;
        const dx = Math.cos(rad);
        const dy = Math.sin(rad);
        const screenPos = paper.view.projectToView(new paper.Point(x, y));

        if (mouseViewPos.getDistance(screenPos) <= 15) {
          isOverHandle = true;
          break;
        }

        const hx = screenPos.x + dx * 40;
        const hy = screenPos.y + dy * 40;
        if (mouseViewPos.getDistance(new paper.Point(hx, hy)) <= 15) {
          isOverHandle = true;
          break;
        }

        const dhx = screenPos.x - dx * 40;
        const dhy = screenPos.y - dy * 40;
        if (mouseViewPos.getDistance(new paper.Point(dhx, dhy)) <= 15) {
          isOverHandle = true;
          break;
        }

        const ihx = screenPos.x - dx * 80;
        const ihy = screenPos.y - dy * 80;
        if (mouseViewPos.getDistance(new paper.Point(ihx, ihy)) <= 15) {
          isOverHandle = true;
          break;
        }
      }

      if (isOverHandle) {
        if (paper.view.element.style.cursor !== 'default') {
          paper.view.element.style.cursor = 'default';
        }
      } else {
        if (paper.view.element.style.cursor === 'default') {
          paper.view.element.style.cursor = '';
        }
      }
    };

    tool.onMouseDown = (event) => {
      const isRightClick = event.event.button === 2;
      const isAltClick = isAltPressedRef.current && event.event.button === 0;
      const isMiddleClick = event.event.button === 1;

      const isOnRuler = event.event.offsetX <= RULER_SIZE || event.event.offsetY <= RULER_SIZE;

      if (activeToolRef.current === 'select' && !isRightClick && !isMiddleClick && !isOnRuler) {
        const zoom = paper.view.zoom;
        const bounds = paper.view.bounds;
        const guides = customGuidesRef.current;
        const mouseViewPos = new paper.Point(event.event.offsetX, event.event.offsetY);

        for (const guide of guides) {
          const {x, y, angle = 0, id} = guide;
          const rad = (angle * Math.PI) / ANGLE_COEFFICIENT;
          const dx = Math.cos(rad);
          const dy = Math.sin(rad);

          const screenPos = paper.view.projectToView(new paper.Point(x, y));

          const distMove = mouseViewPos.getDistance(screenPos);
          if (distMove <= 15) {
            selectionStateRef.current.draggedGuide = {id, mode: 'move'};
            return;
          }

          const hx = screenPos.x + dx * 40;
          const hy = screenPos.y + dy * 40;
          const distRotate = mouseViewPos.getDistance(new paper.Point(hx, hy));
          if (distRotate <= 15) {
            selectionStateRef.current.draggedGuide = {id, mode: 'rotate'};
            return;
          }

          const dhx = screenPos.x - dx * 40;
          const dhy = screenPos.y - dy * 40;
          const distDelete = mouseViewPos.getDistance(new paper.Point(dhx, dhy));
          if (distDelete <= 15) {
            setCustomGuidesRef.current(prev => prev.filter(g => g.id !== id));
            onProjectChangeRef.current?.();
            return;
          }

          const ihx = screenPos.x - dx * 80;
          const ihy = screenPos.y - dy * 80;
          const distInput = mouseViewPos.getDistance(new paper.Point(ihx, ihy));
          if (distInput <= 15) {
            setTimeout(() => {
              const newAngleStr = prompt(tRef.current?.('guides.enter_angle') || 'Введіть кут нахилу лінії (0-360):', angle.toFixed(2));
              if (newAngleStr !== null) {
                const newAngle = parseFloat(newAngleStr);
                if (!isNaN(newAngle)) {
                  setCustomGuidesRef.current(prev =>
                    prev.map(g => g.id === id ? {...g, angle: newAngle} : g)
                  );
                  onProjectChangeRef.current?.();
                } else {
                  alert(tRef.current?.('guides.invalid_angle') || 'Будь ласка, введіть коректне числове значення кута.');
                }
              }
            }, 10);
            return;
          }
        }
      }

      if (isRightClick || isMiddleClick || activeToolRef.current === 'hand') {
        isPanningRef.current = true;
        setIsPanning(true);
        return;
      }

      if (activeToolRef.current === 'text') {
        return;
      }

      if (isAltClick && activeToolRef.current === 'select') {
        const altHit = paper.project.hitTest(event.point, {
          fill: true,
          stroke: true,
          segments: true,
          tolerance: 10 / paper.view.zoom,
          match: (hit) => {
            const item = hit.item;
            const isSystem = item.guide || item.data?.isGuide || item.data?.isSystemOverlay;
            const isVisible = item.visible && item.layer?.name !== 'backgroundLayer';
            return isVisible && !isSystem && !item.data?.isTransformHandle;
          },
        });

        if (altHit && altHit.item && !altHit.item.locked) {
          if (!altHit.item.selected) {
            clearSelection();
            altHit.item.selected = true;
            updateTransformUI();
            onProjectChange?.();
          }
          duplicateDragRef.current = {armed: true, triggered: false};
          return;
        }
      }

      const handleHit = paper.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        guides: true,
        tolerance: 12 / paper.view.zoom,
        match: (hit) => hit.item.visible && hit.item.data?.isTransformHandle,
      });

      if (handleHit) {
        const action = handleHit.item.data.action;
        const selectedItems = paper.project.selectedItems.filter(
          (item) => item.visible && !item.data?.isGuide && !item.guide
        );

        if (selectedItems.length > 0) {
          let bounds = selectedItems[0].bounds.clone();
          for (let i = 1; i < selectedItems.length; i++) {
            bounds = bounds.unite(selectedItems[i].bounds);
          }

          transformStateRef.current = {
            action,
            initialBounds: bounds,
            initialCenter: bounds.center.clone(),
            initialPoint: event.point.clone(),
            initialAngle: event.point.subtract(bounds.center).angle,
          };
          return;
        }
      }

      transformStateRef.current.action = null;

      if (SHAPE_TOOLS.includes(activeToolRef.current)) {
        clearSelection();
        updateTransformUI();
        onProjectChange?.();
        drawingRef.current = {
          type: activeToolRef.current,
          start: event.point.clone(),
          item: null,
        };
        return;
      }

      const hitResult = paper.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        segments: true,
        tolerance: 10 / paper.view.zoom,
        match: (hit) => {
          const item = hit.item;
          const isSystem = item.guide || item.data?.isGuide || item.data?.isSystemOverlay;
          const isVisible = item.visible && item.layer?.name !== 'backgroundLayer';
          return isVisible && !isSystem && !item.data?.isTransformHandle;
        },
      });

      const hitValidItem = hitResult && hitResult.item && !hitResult.item.locked;

      if (activeToolRef.current === 'select') {
        if (event.modifiers.alt && hitResult?.item?.data?.isGuide) {
          hitResult.item.remove();
          updateTransformUI();
          onProjectChange?.();
          window.dispatchEvent(new CustomEvent('paper-item-deleted'));
          return;
        }

        if (hitValidItem) {
          const item = hitResult.item;

          if (event.modifiers.shift) {
            item.selected = !item.selected;
          } else if (!item.selected) {
            clearSelection();
            item.selected = true;
          }
        } else {
          const selectedItems = paper.project.selectedItems.filter(
            (i) => i.visible && !i.data?.isGuide && !i.guide
          );

          let isClickInsideSelectionBounds = false;

          if (selectedItems.length > 1) {
            let groupBounds = selectedItems[0].bounds.clone();
            for (let i = 1; i < selectedItems.length; i++) {
              groupBounds = groupBounds.unite(selectedItems[i].bounds);
            }

            if (groupBounds.contains(event.point)) {
              isClickInsideSelectionBounds = true;
            }
          }

          if (!isClickInsideSelectionBounds && !isOnRuler) {
            if (!event.modifiers.shift) {
              clearSelection();
            }
            selectionStartPoint = event.point.clone();
            isBoxSelecting = true;
          }
        }

        updateTransformUI();
        onProjectChange?.();
      } else if (activeToolRef.current === 'pen') {
        clearSelection();
        const penPath = new paper.Path({
          segments: [event.point],
          strokeColor: '#ef4444',
          strokeWidth: 1.5 / paper.view.zoom,
          fillColor: new paper.Color(239, 68, 68, 0.001),
        });
        
        if (viewModeRef.current === 'vector') {
          penPath.data.origStyle = {
            fillColor: penPath.fillColor.toCSS(true),
            strokeColor: penPath.strokeColor.toCSS(true),
            strokeWidth: penPath.strokeWidth
          };
          penPath.fillColor = new paper.Color(0, 0, 0, 0.001);
          penPath.strokeColor = '#cbd5e1';
          penPath.strokeWidth = 1 / paper.view.zoom;
          penPath.strokeScaling = false;
        }

        currentPathRef.current = penPath;
        currentPathRef.current.name = 'Лінія';
        onProjectChange?.();
      } else if (activeToolRef.current === 'text') {
      }
    };

    tool.onMouseDrag = (event) => {
      if (selectionStateRef.current.draggedGuide) {
        const {id, mode} = selectionStateRef.current.draggedGuide;
        setCustomGuidesRef.current((prev) =>
          prev.map((g) => {
            if (g.id !== id) return g;
            if (mode === 'move') {
              return {...g, x: g.x + event.delta.x, y: g.y + event.delta.y};
            } else {
              const centerView = paper.view.projectToView(new paper.Point(g.x, g.y));
              const mouseViewPos = new paper.Point(event.event.offsetX, event.event.offsetY);
              const angle = Math.atan2(mouseViewPos.y - centerView.y, mouseViewPos.x - centerView.x) * (ANGLE_COEFFICIENT / Math.PI);
              return {...g, angle};
            }
          })
        );
        window.dispatchEvent(new CustomEvent('paper-item-transform'));
        return;
      }

      if (isPanningRef.current) {
        const nativeEvent = event.event;
        const zoom = paper.view.zoom;
        const dx = (nativeEvent.movementX || 0) / zoom;
        const dy = (nativeEvent.movementY || 0) / zoom;

        if (dx !== 0 || dy !== 0) {
          paper.view.center = paper.view.center.subtract(new paper.Point(dx, dy));
          drawBackgroundCanvas();
          drawRulers();
        }
        return;
      }

      if (drawingRef.current) {
        const {type, start, item} = drawingRef.current;
        if (item) item.remove();
        const newItem = createShapeFromDrag(type, start, event.point);
        
        if (viewModeRef.current === 'vector') {

          const isLineOrArrow = type === 'line' || type === 'arrow';
          
          newItem.data = {
            ...newItem.data,
            origStyle: {
              fillColor: isLineOrArrow ? null : FILL_COLOR,
              strokeColor: VECTOR_STROKE_COLOR,
              strokeWidth: 1,
              strokeScaling: false
            }
          };
          
          if (type === 'arrow' && newItem.children) {
            newItem.children.forEach(child => {
              child.data = child.data || {};
              child.data.origStyle = {
                fillColor: child.closed ? VECTOR_STROKE_COLOR : null,
                strokeColor: VECTOR_STROKE_COLOR,
                strokeWidth: 1,
                strokeScaling: false
              };
              child.fillColor = new paper.Color(0, 0, 0, 0.001);
              child.strokeColor = themeRef.current === 'dark' ? '#cbd5e1' : '#334155';
              child.strokeWidth = 1 / paper.view.zoom;
              child.strokeScaling = false;
            });
          } else {
            newItem.fillColor = new paper.Color(0, 0, 0, 0.001);
            newItem.strokeColor = themeRef.current === 'dark' ? '#cbd5e1' : '#334155';
            newItem.strokeWidth = 1 / paper.view.zoom;
            newItem.strokeScaling = false;
          }
        }
        
        drawingRef.current.item = newItem;
        paper.view.update();
        scheduleProjectChange();
        return;
      }

      if (isBoxSelecting && selectionStartPoint && !selectionStateRef.current.draggedGuide) {
        const zoom = paper.view.zoom;
        if (selectionRect) selectionRect.remove();

        const rectBounds = new paper.Rectangle(selectionStartPoint, event.point);
        selectionRect = new paper.Path.Rectangle(rectBounds);
        selectionRect.name = 'selectionRect';
        selectionRect.data = {isSystemOverlay: true};
        selectionRect.strokeColor = new paper.Color('#06b6d4');
        selectionRect.strokeWidth = 1 / zoom;
        selectionRect.dashArray = [4 / zoom, 4 / zoom];
        selectionRect.fillColor = new paper.Color(6, 182, 212, 0.05);

        paper.project.activeLayer.children.forEach((item) => {
          const isSystem =
            item.name === 'transformOverlay' ||
            item === selectionRect ||
            item.locked ||
            !item.visible ||
            item.guide ||
            item.data?.isGuide ||
            item.data?.isSystemOverlay ||
            item.data?.isTransformHandle;

          if (isSystem) {
            return;
          }

          const isInside = rectBounds.contains(item.bounds);
          const isIntersects = rectBounds.intersects(item.bounds);
          if (isInside || isIntersects) {
            item.selected = true;
          } else if (!event.modifiers.shift) {
            item.selected = false;
          }
        });

        updateTransformUI();
        return;
      }

      const action = transformStateRef.current.action;
      if (action) {
        const selected = paper.project.selectedItems.filter(
          (item) => item.visible && !item.locked && !item.guide && !item.data?.isGuide
        );

        if (selected.length === 0) return;

        if (action === 'rotate') {
          const center = transformStateRef.current.initialCenter;
          const currentAngle = event.point.subtract(center).angle;
          const deltaAngle = currentAngle - transformStateRef.current.initialAngle;

          selected.forEach((item) => {
            item.rotate(deltaAngle, center);
            const prevRot = item.data?.rotation || 0;
            item.data = { ...item.data, rotation: (prevRot + deltaAngle) % 360 };
          });

          transformStateRef.current.initialAngle = currentAngle;
        } else if (action.startsWith('scale')) {
          const bounds = transformStateRef.current.initialBounds;
          const initialPt = transformStateRef.current.initialPoint;

          let origin = bounds.bottomRight;
          if (action === 'scale-tr') origin = bounds.bottomLeft;
          if (action === 'scale-bl') origin = bounds.topRight;
          if (action === 'scale-br') origin = bounds.topLeft;
          if (action === 'scale-tc') origin = bounds.bottomCenter;
          if (action === 'scale-bc') origin = bounds.topCenter;
          if (action === 'scale-ml') origin = bounds.rightCenter;
          if (action === 'scale-mr') origin = bounds.leftCenter;

          let scaleX = 1;
          let scaleY = 1;

          if (event.modifiers.control) {
            const oldDist = initialPt.getDistance(origin);
            const newDist = event.point.getDistance(origin);
            const uniformScale = oldDist > 0.001 ? newDist / oldDist : 1;
            scaleX = uniformScale;
            scaleY = uniformScale;
          } else {
            const oldDistX = Math.abs(initialPt.x - origin.x);
            const oldDistY = Math.abs(initialPt.y - origin.y);
            const newDistX = Math.abs(event.point.x - origin.x);
            const newDistY = Math.abs(event.point.y - origin.y);

            const isVerticalOnly = action === 'scale-tc' || action === 'scale-bc';
            const isHorizontalOnly = action === 'scale-ml' || action === 'scale-mr';

            if (!isVerticalOnly) {
              scaleX = oldDistX > 0.001 ? newDistX / oldDistX : 1;
            }
            if (!isHorizontalOnly) {
              scaleY = oldDistY > 0.001 ? newDistY / oldDistY : 1;
            }
          }

          selected.forEach((item) => {
            item.scale(scaleX, scaleY, origin);
          });

          let newBounds = selected[0].bounds.clone();
          for (let i = 1; i < selected.length; i++) {
            newBounds = newBounds.unite(selected[i].bounds);
          }

          transformStateRef.current.initialPoint = event.point.clone();
          transformStateRef.current.initialBounds = newBounds;
        }

        updateTransformUI();
        scheduleProjectChange();
        return;
      }

      if (activeToolRef.current === 'select') {
        if (duplicateDragRef.current.armed && !duplicateDragRef.current.triggered) {
          const originals = paper.project.selectedItems.filter(
            (item) => item.visible && !item.locked && !item.guide && !item.data?.isGuide
          );

          const clones = originals.map((item) => {
            const clone = item.clone({insert: true, deep: true});
            item.selected = false;
            return clone;
          });

          clones.forEach((clone) => {
            clone.selected = true;
          });

          duplicateDragRef.current.triggered = true;
          updateTransformUI();
        }

        const selected = paper.project.selectedItems.filter(
          (item) => item.visible && !item.locked && !item.guide && !item.data?.isGuide
        );

        if (selected.length > 0) {
          const finalDelta = calculateSnapDelta(selected, event.delta);
          selected.forEach((item) => {
            item.position = item.position.add(finalDelta);
          });

          updateTransformUI();
          scheduleProjectChange();
        }
      } else if (activeToolRef.current === 'pen' && currentPathRef.current) {
        currentPathRef.current.add(event.point);
        paper.view.update();
      }
    };

    tool.onMouseUp = (event) => {
      if (selectionStateRef.current.draggedGuide) {
        const {id, mode} = selectionStateRef.current.draggedGuide;
        const guides = customGuidesRef.current;
        const guide = guides.find(g => g.id === id);

        if (guide) {
          const screenPos = paper.view.projectToView(new paper.Point(guide.x, guide.y));

          if (screenPos.x <= RULER_SIZE + 5 || screenPos.y <= RULER_SIZE + 5) {
            setCustomGuidesRef.current(prev => prev.filter(g => g.id !== id));
          }
        }

        selectionStateRef.current.draggedGuide = null;
        onProjectChangeRef.current?.();
        return;
      }

      if (isPanningRef.current) {
        isPanningRef.current = false;
        setIsPanning(false);
        drawBackgroundCanvas();
        drawRulers();
        return;
      }

      resetGuidesHighlight();
      transformStateRef.current.action = null;
      duplicateDragRef.current = {armed: false, triggered: false};

      if (selectionRect) {
        selectionRect.remove();
        selectionRect = null;
      }
      isBoxSelecting = false;
      selectionStartPoint = null;

      if (drawingRef.current) {
        const {type, start, item} = drawingRef.current;
        let finalItem = item;

        const dragDistance = item ? start.getDistance(event.point) : 0;

        if (type === 'polygon-n') {
          if (item) item.remove();
          drawingRef.current = null;

          setTimeout(() => {
            const sidesStr = prompt(tRef.current('guides.enter_sides_count'), '5');
            if (sidesStr === null) {
              activeToolRef.current = 'select';
              setActiveToolRef.current?.('select');
              paper.view.update();
              return;
            }
            const sides = parseInt(sidesStr, 10) || 5;
            const fallbackStart = start.subtract(new paper.Point(20, 20));
            const fallbackEnd = start.add(new paper.Point(20, 20));

            const boundsRect = (item && dragDistance >= MIN_DRAG_DISTANCE)
              ? item.bounds.clone()
              : new paper.Rectangle(fallbackStart, fallbackEnd);

            const newItem = new paper.Path.RegularPolygon({
              center: boundsRect.center,
              sides: sides,
              radius: Math.min(boundsRect.width, boundsRect.height) / 2
            });
            newItem.strokeColor = new paper.Color(VECTOR_STROKE_COLOR);
            newItem.strokeWidth = 1;
            newItem.strokeScaling = false;
            newItem.fillColor = new paper.Color(FILL_COLOR);
            newItem.data = { ...newItem.data, type };
            
            if (viewModeRef.current === 'vector') {
              const isLineOrArrow = type === 'line' || type === 'arrow';
              newItem.data.origStyle = {
                fillColor: isLineOrArrow ? null : FILL_COLOR,
                strokeColor: VECTOR_STROKE_COLOR,
                strokeWidth: 1,
                strokeScaling: false
              };
              
              if (type === 'arrow' && newItem.children) {
                newItem.children.forEach(child => {
                  child.data = child.data || {};
                  child.data.origStyle = {
                    fillColor: child.closed ? VECTOR_STROKE_COLOR : null,
                    strokeColor: VECTOR_STROKE_COLOR,
                    strokeWidth: 1,
                    strokeScaling: false
                  };
                  child.fillColor = new paper.Color(0, 0, 0, 0.001);
                  child.strokeColor = themeRef.current === 'dark' ? '#cbd5e1' : '#334155';
                  child.strokeWidth = 1 / paper.view.zoom;
                  child.strokeScaling = false;
                });
              } else {
                newItem.fillColor = new paper.Color(0, 0, 0, 0.001);
                newItem.strokeColor = themeRef.current === 'dark' ? '#cbd5e1' : '#334155';
                newItem.strokeWidth = 1 / paper.view.zoom;
                newItem.strokeScaling = false;
              }
            }
            
            clearSelection();
            newItem.selected = true;
            window.dispatchEvent(new CustomEvent('paper-item-transform'));
            updateTransformUI();
            onProjectChangeRef.current?.();
            paper.view.update();

            activeToolRef.current = 'select';
            setActiveToolRef.current?.('select');
          }, 10);
          return;
        }

        if (!finalItem || dragDistance < MIN_DRAG_DISTANCE) {
          if (finalItem) finalItem.remove();
          const fallbackStart = start.subtract(new paper.Point(20, 20));
          const fallbackEnd = start.add(new paper.Point(20, 20));
          finalItem = createShapeFromDrag(type, fallbackStart, fallbackEnd);
        }

        if (viewModeRef.current === 'vector' && finalItem) {
          const applyVectorStylesRecursive = (item) => {
            if (item.data?.isSystem || item.data?.isTransformHandle) return;

            if (item instanceof paper.Group) {
              item.children.forEach(applyVectorStylesRecursive);
              return;
            }

            if (!item.data) item.data = {};

            if (!item.data.origStyle) {
              const isLineOrArrow = type === 'line' || type === 'arrow';
              const isArrowHead = item.closed && type === 'arrow';
              
              item.data.origStyle = {
                fillColor: isLineOrArrow ? (isArrowHead ? VECTOR_STROKE_COLOR : null) : FILL_COLOR,
                strokeColor: VECTOR_STROKE_COLOR,
                strokeWidth: 1,
                strokeScaling: false
              };
            }

            item.fillColor = new paper.Color(0, 0, 0, 0.001);
            item.strokeColor = themeRef.current === 'dark' ? '#cbd5e1' : '#334155';
            item.strokeWidth = 1 / paper.view.zoom;
            item.strokeScaling = false;
          };
          
          applyVectorStylesRecursive(finalItem);
        }

        clearSelection();
        if (finalItem) {
          finalItem.selected = true;
          window.dispatchEvent(new CustomEvent('paper-item-transform'));
        }

        drawingRef.current = null;

        updateTransformUI();
        onProjectChange?.();

        activeToolRef.current = 'select';
        setActiveToolRef.current?.('select');
        return;
      }

      if (activeToolRef.current === 'pen' && currentPathRef.current) {
        currentPathRef.current.simplify(10);
        currentPathRef.current.selected = true;
        window.dispatchEvent(new CustomEvent('paper-item-transform'));
        currentPathRef.current = null;
        updateTransformUI();
        onProjectChange?.();

        activeToolRef.current = 'select';
        setActiveToolRef.current?.('select');
        return;
      }

      if (activeToolRef.current === 'text') {
        const dragDistance = event.point.getDistance(event.downPoint);
        if (dragDistance < MIN_DRAG_DISTANCE) {
          clearSelection();
          addTextAsPath(event.point);
        }
        return;
      }

      updateTransformUI();
      onProjectChange?.();
    };

    const handleKeyDown = (e) => {
      const isInputActive = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);

      if (
        (e.altKey || e.code === 'AltLeft' || e.code === 'AltRight' || e.key === 'Alt') &&
        !isInputActive
      ) {
        isAltPressedRef.current = true;
        if (tool.lastEvent) {
          tool.onMouseMove(tool.lastEvent);
        }
      }

      if (
        (e.code === 'ControlLeft' || e.code === 'ControlRight' || e.key === 'Control' || e.metaKey) &&
        !isInputActive
      ) {

      }

      if (e.code === 'Space' && !isInputActive) {
        isSpacePressedRef.current = true;
      }

      if ((e.code === 'Delete' || e.code === 'Backspace') && !isInputActive) {
        const selectedItems = paper.project.selectedItems;
        if (selectedItems.length > 0) {
          selectedItems.forEach((item) => item.remove());
          updateTransformUI();
          onProjectChange?.();
          window.dispatchEvent(new CustomEvent('paper-item-deleted'));
        }
      }
    };

    const handleKeyUp = (e) => {
      const isInputActive = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
      if ((e.code === 'AltLeft' || e.code === 'AltRight' || e.key === 'Alt') && !isInputActive) {
        isAltPressedRef.current = false;
        if (tool.lastEvent) {
          tool.onMouseMove(tool.lastEvent);
        }
        if (paper.view?.element && paper.view.element.style.cursor === 'copy') {
          paper.view.element.style.cursor = '';
        }
      }
      if (e.code === 'ControlLeft' || e.code === 'ControlRight' || e.key === 'Control' || !e.metaKey) {

      }
      if (e.code === 'Space') {
        isSpacePressedRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
      canvas.removeEventListener('dblclick', handleDblClick);
      tool.remove();
      paper.view.element.style.cursor = '';
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    onProjectChange,
    drawBackgroundCanvas,
    drawRulers,
    updateTransformUI,
    calculateSnapDelta,
    resetGuidesHighlight,
    addTextAsPath,
  ]);

  return {isPanning};
}